import { z } from "zod";

/** Backend-owned operation; synthetic fixtures can vary source data between scheduling boundaries. */
export enum SchedulingInsuranceOperation {
  ELIGIBILITY = "eligibility",
  AVAILABILITY = "availability",
  BOOKING = "booking",
  RESCHEDULING = "rescheduling",
}

export const schedulingInsuranceOperationSchema = z.nativeEnum(SchedulingInsuranceOperation);

const nullableText = z.string().trim().min(1).nullable();

export const schedulingEligibilitySnapshotSchema = z.object({
  status: z.enum(["eligible", "ineligible", "unknown"]),
  checkedDate: z.string().date().nullable(),
});

export function normalizeInsuranceMemberId(memberId: string | null | undefined): string | null {
  const normalized =
    memberId
      ?.trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "") ?? "";

  return normalized || null;
}

/** Recorded policy facts; requirements and scheduling clearance are derived separately. */
export const insurancePolicySnapshotSchema = z.object({
  insuranceId: z.string().trim().min(1),
  insurancePackageId: nullableText,
  sequence: z.number().int().positive().nullable(),
  subscriberId: nullableText,
  payerName: nullableText,
  planName: nullableText,
  effectiveDate: z.string().date().nullable(),
  expirationDate: z.string().date().nullable(),
  ehrCancelledAt: nullableText,
  selfPay: z.boolean(),
  casePolicy: z.boolean(),
  eligibilityStatus: nullableText,
  eligibilityReason: nullableText,
  eligibilityMessage: nullableText,
  eligibilityLastChecked: nullableText,
  schedulingEligibility: schedulingEligibilitySnapshotSchema.optional(),
});

export const insuranceCoverageReadSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("complete"), policies: z.array(insurancePolicySnapshotSchema).max(100) }),
  z.object({ status: z.literal("partial"), policies: z.array(insurancePolicySnapshotSchema).max(100) }),
  z.object({ status: z.literal("failed"), reason: z.enum(["unavailable", "invalid_response"]) }),
]);

/** Recorded EHR authorization facts; an authorization number is not approval evidence. */
export const insuranceAuthorizationSnapshotSchema = z.object({
  id: z.string().trim().min(1),
  insuranceId: z.string().trim().min(1),
  number: nullableText,
  providerEhrId: nullableText,
  locationEhrId: nullableText,
  startDate: z.string().date().nullable(),
  endDate: z.string().date().nullable(),
  expired: z.boolean().nullable(),
  visitsRemaining: z.number().finite().nullable(),
  units: nullableText,
  serviceCodes: z.array(z.string().trim().min(1)).max(100),
});

export const insuranceAuthorizationReadSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("complete"), authorizations: z.array(insuranceAuthorizationSnapshotSchema).max(100) }),
  z.object({ status: z.literal("partial"), authorizations: z.array(insuranceAuthorizationSnapshotSchema).max(100) }),
  z.object({ status: z.literal("failed"), reason: z.enum(["unavailable", "invalid_response"]) }),
]);

export type InsurancePolicySnapshot = z.infer<typeof insurancePolicySnapshotSchema>;
export type InsuranceCoverageRead = z.infer<typeof insuranceCoverageReadSchema>;
export type InsuranceAuthorizationRead = z.infer<typeof insuranceAuthorizationReadSchema>;
